import React, { useContext, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

import { ugcInstagramPath, ugcReviewsPath } from '@/common/app/path'
import { fetchRest } from '@/common/api/fetcher'

// カード共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
// 入力欄は標準 TextField / Textarea と同じトークンで揃える（枠: border-input）
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'

/** 摩耗検知APIが返す形（backend/routers/ugc_analytics.py の wear-detection） */
interface WearAlert {
  severity: string
  message: string
  detail?: string
}

interface WornItem {
  ugc_item_id: number
  prev_ctr: number
  recent_ctr: number
  decline_pct: number
  // 2026-09-29 追加：どの投稿か見分け、その場で非公開にするための情報
  source_type?: string
  status?: string
  caption?: string
  thumbnail_url?: string | null
  author_name?: string | null
  product_external_id?: string | null
  ai_score?: number | null
  review_id?: number | null
  replacement?: { ugc_item_id: number; source_type: string; ai_score: number | null; caption: string } | null
}

const SOURCE_LABEL: Record<string, string> = { review: 'レビュー', instagram: 'Instagram' }

/** services/ugc_wear_detector.detect_wear が返す overall */
interface WearOverall {
  recent_ctr: number
  prev_ctr: number
  decline_pct: number
  recent_impressions: number
  recent_clicks: number
  prev_impressions: number
  prev_clicks: number
}

const EMPTY_OVERALL: WearOverall = {
  recent_ctr: 0,
  prev_ctr: 0,
  decline_pct: 0,
  recent_impressions: 0,
  recent_clicks: 0,
  prev_impressions: 0,
  prev_clicks: 0,
}

interface WearDetection {
  overall?: WearOverall
  alerts?: WearAlert[]
  worn_items?: WornItem[]
  recommendations?: string[]
}

interface TrendPoint {
  date: string
  views: number
  clicks: number
}

interface TrendResponse {
  trend?: TrendPoint[]
}

export default function UGCWearDetection() {
  const { siteId } = useParams()
  const [detection, setDetection] = useState<WearDetection | null>(null)
  const [trend, setTrend] = useState<TrendResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(30)
  // 非公開にしている最中の投稿（押した行のボタンだけを回す）
  const [hidingId, setHidingId] = useState<number | null>(null)
  const { addMessage } = useContext(ToastMessageContext)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      fetchRest<WearDetection>(`/api/sites/${siteId}/ugc/analytics/wear-detection`),
      fetchRest<TrendResponse>(`/api/sites/${siteId}/ugc/analytics/trend?days=${days}`),
    ])
      .then(([d, t]) => { setDetection(d); setTrend(t) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId, days])

  const ov = detection?.overall ?? EMPTY_OVERALL
  const alerts = detection?.alerts ?? []
  const worn = detection?.worn_items ?? []
  const recs = detection?.recommendations ?? []
  const trendPoints = trend?.trend ?? []
  const sid = Number(siteId)

  /**
   * 低下した投稿を非公開にする。
   * レビューはレビュー側で変える（表示単位も連動する）。表示単位だけ変えると、
   * 投稿レビュー管理では「公開」のまま残って食い違うため。
   */
  const handleHide = (w: WornItem) => {
    setHidingId(w.ugc_item_id)
    const url = w.review_id
      ? `/api/sites/${siteId}/ugc/reviews/${w.review_id}`
      : `/api/sites/${siteId}/ugc/items/${w.ugc_item_id}`
    fetchRest(url, { method: 'PATCH', body: JSON.stringify({ status: 'hidden' }) })
      .then(() => {
        setDetection(prev =>
          prev
            ? {
                ...prev,
                worn_items: (prev.worn_items ?? []).map(x =>
                  x.ugc_item_id === w.ugc_item_id ? { ...x, status: 'hidden' } : x
                )
              }
            : prev
        )
        addMessage({
          text: `UGC #${w.ugc_item_id} を非公開にしました（サイトに表示されなくなります）`,
          type: MessageType.SUCCESS
        })
      })
      .catch(() =>
        addMessage({ text: '非公開にできませんでした。時間をおいて再度お試しください', type: MessageType.CRITICAL })
      )
      .finally(() => setHidingId(null))
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='⚠️ コンテンツ鮮度アラート'
        description='表示し続けて見飽きられたUGC（クリック率が落ちた投稿）を見つけ、非公開や入れ替えを提案します'
        rightNode={
          <select value={days} onChange={e => setDays(Number(e.target.value))} className={INPUT}>
            <option value={14}>14日間</option>
            <option value={30}>30日間</option>
            <option value={60}>60日間</option>
          </select>
        }
      />

      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : (
        <>
          {/* アラート */}
          {alerts.length > 0 ? (
            <div className='mb-4 flex flex-col gap-2.5'>
              {alerts.map((alert, i) => (
                <div
                  key={i}
                  className={`${CARD} p-4`}
                  style={{ borderLeft: `4px solid ${alert.severity === 'critical' ? 'var(--color-fill-critical)' : 'var(--color-fill-warning)'}` }}
                >
                  <div className='flex items-center gap-2.5'>
                    <span className='text-xl'>{alert.severity === 'critical' ? '🔴' : '🟡'}</span>
                    <div className='flex-1'>
                      <div className='text-primary text-sm font-semibold'>{alert.message}</div>
                      <div className='text-secondary mt-0.5 text-xs'>{alert.detail}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className={`${CARD} mb-4 p-4`}
              style={{ borderLeft: '4px solid var(--color-fill-success)' }}
            >
              <div className='flex items-center gap-2.5'>
                <span className='text-xl'>🟢</span>
                <div>
                  <div className='text-primary text-sm font-semibold'>UGCの効果は安定しています</div>
                  <div className='text-secondary mt-0.5 text-xs'>直近のCTR低下は検出されていません</div>
                </div>
              </div>
            </div>
          )}

          {/* 全体CTR比較 */}
          <div className='mb-4 grid grid-cols-3 gap-3'>
            <div className={`${CARD} p-4 text-center`}>
              <div className='text-secondary text-[11px]'>今週CTR</div>
              <div className={`text-[28px] font-bold ${ov.recent_ctr > 0 ? 'text-primary' : 'text-tertiary'}`}>{ov.recent_ctr || 0}%</div>
              <div className='text-tertiary text-[10px]'>{(ov.recent_impressions || 0).toLocaleString()} imp / {ov.recent_clicks || 0} click</div>
            </div>
            <div className={`${CARD} p-4 text-center`}>
              <div className='text-secondary text-[11px]'>前週CTR</div>
              <div className='text-tertiary text-[28px] font-bold'>{ov.prev_ctr || 0}%</div>
              <div className='text-tertiary text-[10px]'>{(ov.prev_impressions || 0).toLocaleString()} imp / {ov.prev_clicks || 0} click</div>
            </div>
            <div className={`${CARD} p-4 text-center`}>
              <div className='text-secondary text-[11px]'>変化率</div>
              <div className={`text-[28px] font-bold ${ov.decline_pct > 10 ? 'text-critical' : ov.decline_pct > 0 ? 'text-warning' : 'text-success'}`}>
                {ov.decline_pct > 0 ? '-' : '+'}{Math.abs(ov.decline_pct || 0)}%
              </div>
            </div>
          </div>

          {/* CTRトレンド */}
          <div className={`${CARD} mb-4 p-5`}>
            <h3 className='text-primary m-0 text-sm font-semibold'>CTR推移（日別）</h3>
            {trendPoints.length > 0 ? (
              <div className='mt-3 flex h-[140px] items-end gap-[3px]'>
                {trendPoints.map(d => {
                  const maxViews = Math.max(...trendPoints.map(x => x.views), 1)
                  const barH = Math.max(4, d.views / maxViews * 120)
                  const ctr = d.views > 0 ? d.clicks / d.views * 100 : 0
                  return (
                    <div key={d.date} className='flex flex-1 flex-col items-center gap-0.5'>
                      <span className={`text-[8px] font-semibold ${ctr > 0 ? 'text-success' : 'text-tertiary'}`}>{ctr > 0 ? `${ctr.toFixed(1)}%` : ''}</span>
                      <div
                        className={`w-full rounded-[3px] ${ctr > 2 ? 'bg-fill-success' : ctr > 0 ? 'bg-fill-warning' : 'bg-fill-tertiary'}`}
                        style={{ height: barH }}
                      />
                      <span className='text-tertiary text-[7px]'>{d.date.slice(8)}</span>
                    </div>
                  )
                })}
              </div>
            ) : <p className='text-tertiary mt-3 text-[13px]'>データなし</p>}
          </div>

          <div className='grid grid-cols-2 gap-4'>
            {/* 摩耗UGC一覧。2026-09-29 修正：どの投稿か分かるようにし、その場で非公開・遷移できるようにした */}
            <div className={`${CARD} p-5`}>
              <h3 className='text-primary m-0 text-sm font-semibold'>CTRが低下したUGC ({worn.length}件)</h3>
              <p className='text-tertiary mt-1 mb-0 text-[11px]'>
                前週と比べて直近7日のクリック率が40%以上落ちた投稿です。見飽きられている可能性があります。
              </p>
              {worn.length > 0 ? (
                <div className='mt-2.5 flex flex-col gap-2'>
                  {worn.map(w => {
                    const hidden = w.status === 'hidden'
                    const openPath =
                      w.source_type === 'instagram'
                        ? ugcInstagramPath(sid)
                        : `${ugcReviewsPath(sid)}${w.review_id ? `?review_id=${w.review_id}` : ''}`
                    return (
                      <div key={w.ugc_item_id} className='bg-surface-secondary flex gap-2.5 rounded-lg border border-secondary p-2.5'>
                        {w.thumbnail_url ? (
                          <img src={w.thumbnail_url} alt='' className='h-12 w-12 shrink-0 rounded-md object-cover' />
                        ) : (
                          <span className='bg-surface flex h-12 w-12 shrink-0 items-center justify-center rounded-md text-base'>📝</span>
                        )}
                        <div className='min-w-0 flex-1'>
                          <div className='flex items-center gap-1.5'>
                            <span className='text-primary text-xs font-semibold'>UGC #{w.ugc_item_id}</span>
                            {w.source_type && (
                              <span className='bg-surface text-secondary rounded px-1.5 text-[10px]'>
                                {SOURCE_LABEL[w.source_type] ?? w.source_type}
                              </span>
                            )}
                            {hidden && <span className='bg-surface text-tertiary rounded px-1.5 text-[10px]'>非公開済み</span>}
                          </div>
                          {w.caption && (
                            <div className='text-secondary truncate text-[11px]'>
                              {w.author_name ? `${w.author_name}：` : ''}
                              {w.caption}
                            </div>
                          )}
                          <div className='text-secondary text-[10px]'>
                            CTR: {w.prev_ctr}% → {w.recent_ctr}% (<span className='text-critical'>-{w.decline_pct}%</span>)
                            {w.product_external_id && <> ／ 商品 {w.product_external_id}</>}
                          </div>
                          {w.replacement ? (
                            <div className='text-secondary mt-0.5 text-[10px]'>
                              入れ替え候補：UGC #{w.replacement.ugc_item_id}（AIスコア {w.replacement.ai_score ?? '-'}）
                              {w.replacement.caption}
                            </div>
                          ) : (
                            <div className='text-tertiary mt-0.5 text-[10px]'>
                              同じ商品に入れ替え候補がありません。新しい投稿を集めてください
                            </div>
                          )}
                        </div>
                        <div className='flex shrink-0 flex-col items-end gap-1'>
                          <Button
                            color={ButtonColor.CRITICAL}
                            disabled={hidden || hidingId === w.ugc_item_id}
                            loading={hidingId === w.ugc_item_id}
                            onClick={() => handleHide(w)}
                          >
                            {hidden ? '非公開済み' : '非公開にする'}
                          </Button>
                          <Link to={openPath} className='text-emphasis text-[10px] underline'>
                            {w.source_type === 'instagram' ? 'Instagram UGCで開く' : '投稿レビュー管理で開く'}
                          </Link>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className='text-tertiary mt-2.5 text-[13px]'>CTRが低下したUGCはありません</p>
              )}
            </div>

            {/* 推薦 */}
            <div className={`${CARD} p-5`}>
              <h3 className='text-primary m-0 text-sm font-semibold'>💡 改善推薦</h3>
              <div className='mt-2.5 flex flex-col gap-2'>
                {recs.map((rec, i) => (
                  <div key={i} className='bg-surface-secondary flex gap-2 rounded-lg border border-secondary p-2.5'>
                    <span className='shrink-0 text-sm'>💡</span>
                    <p className='text-secondary m-0 text-xs leading-relaxed'>{rec}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </Main>
  )
}
